import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { UserQuery } from '@core/store/user/user.query';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { V2AirportSearchComponent } from '@v2/shared/airport-search/v2-airport-search.component';
import {
  V2AirportPage,
  V2AirportSearchService,
} from '@v2/shared/airport-search/v2-airport-search.service';
import { EVO_MODAL } from '@v2/shared/evo-modal';
import { AirportModel } from 'app/airport/models/airport.model';
import { Subscription } from 'rxjs';
import { V2BriefingAccessDialogComponent } from '../gate/v2-briefing-access-dialog.component';
import { V2BriefingService } from '../domain/v2-briefing.service';
import { V2BriefingTabsService } from '../page/v2-briefing-tabs.service';

/**
 * The codes the page offers as jump buttons.
 *
 * Real ICAO codes, and the busiest business-aviation fields this desk plans
 * into -- not sample data. They are resolved through the same search endpoint
 * a typed query goes through, because the briefing is addressed by database
 * id and an ICAO code is not one. Resolving rather than hardcoding ids means
 * the list is still right on any deployment.
 */
export const V2_BRIEFING_JUMP_CODES: readonly string[] = [
  'LFPB', 'EGGW', 'LSGG', 'LIML', 'LFMN', 'KTEB',
];

/**
 * The state before an airport is chosen.
 *
 * The thesis of the page, from the mockup README: in this world a place *is*
 * its four-letter code. So the code is the hero -- the search sits on the one
 * inverted surface in the design with real codes as jump buttons directly
 * beneath it, and there is nothing else above the fold to do. No stock runway
 * photography and no invented statistics.
 *
 * Signed in, that is the whole page: headline, search, recents. There is no
 * signed-out variant to build -- every route is inside `AuthGuard` and
 * `AuthGuard` returns false on the server, so the mockup's anonymous column
 * has no state to render against (data-gaps G12). The "What's in a briefing"
 * table and the Request-an-account panel are both signed-out-only and are
 * therefore not here.
 *
 * SSR-safe: no `window`, `document` or `localStorage`.
 */
@Component({
  selector: 'app-v2-briefing-start',
  templateUrl: './v2-briefing-start.component.html',
  styleUrls: ['./v2-briefing-start.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [V2AirportSearchComponent],
})
export class V2BriefingStartComponent {
  private readonly router = inject(Router);
  private readonly search = inject(V2AirportSearchService);
  private readonly modal = inject(NgbModal);
  private readonly userQuery = inject(UserQuery);
  private readonly briefing = inject(V2BriefingService);

  protected readonly tabsState = inject(V2BriefingTabsService);

  private jumpSub: Subscription | null = null;

  protected readonly user = toSignal(this.userQuery.getUser$, { initialValue: null });

  /**
   * No airport is open here, so there is no sample briefing to widen the
   * answer: this is `rights_briefing`, asked through the domain so the page
   * never re-implements the gate.
   */
  protected readonly canReadDetail = computed(() =>
    this.briefing.canReadDetail(this.user(), null));

  protected readonly recents = this.tabsState.recents;
  protected readonly jumpCodes = V2_BRIEFING_JUMP_CODES;

  /** The code currently being resolved, so its button can say so. */
  protected readonly resolving = signal<string | null>(null);
  protected readonly jumpError = signal<string | null>(null);

  constructor() {
    inject(DestroyRef).onDestroy(() => this.jumpSub?.unsubscribe());
  }

  protected open(airport: AirportModel): void {
    const key = this.tabsState.open(airport);
    void this.router.navigate(['/', key]);
  }

  /**
   * A jump code is a search, not a shortcut around one.
   *
   * The briefing endpoint takes a database id; the button carries an ICAO
   * code. Rather than hardcode ids -- which would be wrong on the first
   * deployment that is not this one -- the code goes through the same paginated
   * search the type-ahead uses, and only an exact ICAO match is opened.
   * Anything else is reported rather than guessed at: opening the wrong
   * airport is worse than opening none.
   */
  protected jump(code: string): void {
    if (this.resolving()) { return; }

    this.jumpSub?.unsubscribe();
    this.resolving.set(code);
    this.jumpError.set(null);

    this.jumpSub = this.search.search({ page: 1, page_size: 5, search: code }).subscribe({
      next: (page: V2AirportPage) => {
        this.resolving.set(null);

        const match = (page?.results ?? [])
          .find(airport => (airport.icao ?? '').toUpperCase() === code) ?? null;

        if (match) {
          this.open(match);
        } else {
          this.jumpError.set(`${code} is not in the database.`);
        }
      },
      error: () => {
        this.resolving.set(null);
        this.jumpError.set('Search is not responding. Try again in a moment.');
      },
    });
  }

  protected openAccessDialog(): void {
    this.modal.open(V2BriefingAccessDialogComponent, {
      ...EVO_MODAL,
      ariaLabelledBy: 'v2-briefing-access-title',
    });
  }
}
